// Lightweight helpers for browser push-style notifications, sound, and vibration
// for new chat messages. Works without a service worker - uses the Notification
// API while the tab is open (foreground or backgrounded). Respects user choice
// stored in localStorage so we never nag.

const PERM_ASKED_KEY = "chat-notif-asked";
const ENABLED_KEY = "chat-notif-enabled";
const VIBRATE_KEY = "chat-notif-vibrate";

export const notificationsSupported = () =>
  typeof window !== "undefined" && "Notification" in window;

export const getNotificationPermission = (): NotificationPermission | "unsupported" => {
  if (!notificationsSupported()) return "unsupported";
  return Notification.permission;
};

export const hasAskedForPermission = () =>
  typeof localStorage !== "undefined" && localStorage.getItem(PERM_ASKED_KEY) === "1";

export const markAskedForPermission = () => {
  try { localStorage.setItem(PERM_ASKED_KEY, "1"); } catch {}
};

export const chatNotifEnabled = () => {
  if (!notificationsSupported()) return false;
  if (Notification.permission !== "granted") return false;
  try { return localStorage.getItem(ENABLED_KEY) !== "0"; } catch { return true; }
};

export const setChatNotifEnabled = (on: boolean) => {
  try { localStorage.setItem(ENABLED_KEY, on ? "1" : "0"); } catch {}
};

export const vibrateEnabled = () => {
  try { return localStorage.getItem(VIBRATE_KEY) !== "0"; } catch { return true; }
};

export const setVibrateEnabled = (on: boolean) => {
  try { localStorage.setItem(VIBRATE_KEY, on ? "1" : "0"); } catch {}
};

export const requestChatNotifPermission = async (): Promise<NotificationPermission | "unsupported"> => {
  if (!notificationsSupported()) return "unsupported";
  markAskedForPermission();
  if (Notification.permission === "granted" || Notification.permission === "denied") {
    return Notification.permission;
  }
  try {
    const result = await Notification.requestPermission();
    if (result === "granted") setChatNotifEnabled(true);
    return result;
  } catch {
    return Notification.permission;
  }
};

interface ShowOpts {
  title: string;
  body: string;
  tag?: string;
  icon?: string;
  onClick?: () => void;
}

export const showChatNotification = ({ title, body, tag, icon, onClick }: ShowOpts) => {
  if (!chatNotifEnabled()) return;
  // Skip when the tab is actually focused - UI surfaces will already show it.
  if (typeof document !== "undefined" && document.visibilityState === "visible" && document.hasFocus()) {
    return;
  }
  try {
    const n = new Notification(title, {
      body,
      tag,
      icon: icon || "/favicon.ico",
      badge: "/favicon.ico",
      silent: false,
    });
    n.onclick = () => {
      try { window.focus(); } catch {}
      onClick?.();
      n.close();
    };
  } catch {
    // Some browsers throw if Notification is constructed without a SW (mobile).
    // Fall back silently - the in-app sound + vibration still fire.
  }
};

export const vibratePattern = (pattern: number | number[] = [80, 40, 80]) => {
  if (!vibrateEnabled()) return;
  try {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate(pattern);
    }
  } catch {}
};
